import {useEffect, useRef, useState} from 'react'
import {useApolloClient, useMutation, useQuery} from '@apollo/client/react'

import {ACTIVE_IMPORT, IMPORT_BATCH} from '../../graphql/queries/imports'
import {FOLDERS} from '../../graphql/queries/folders'
import {START_IMPORT} from '../../graphql/mutations/imports'
import {STORAGE} from '../../graphql/queries/storage'
import {UPLOADS} from '../../graphql/queries/uploads'
import {IMPORT_RUNNING, IMPORT_TOO_LARGE, PROVIDER_BUSY, PROVIDER_REFUSED, STORAGE_FULL, says} from '../errorTools'

// The worker copies files in one after another, so a few seconds between asks is soon enough.
const POLL_INTERVAL = 5000

const REFUSALS = [IMPORT_RUNNING, IMPORT_TOO_LARGE, STORAGE_FULL, PROVIDER_BUSY, PROVIDER_REFUSED]

/** What to tell the person when an import couldn't start: the API's own words when it said why. */
export const refusalOf = error => REFUSALS.find(sentence => says(error, sentence)) ?? 'The import couldn’t start. Try again.'

// Where a file stands, in the upload tray's terms: copying in from the provider, being sealed once
// it has arrived, or settled either way.
const phaseOf = ({status, uploadStatus}) => {
  if (status === 'imported') return 'done'
  if (status === 'failed') return 'failed'

  return uploadStatus === 'importing' ? 'importing' : 'encrypting'
}

/** The batch's files as rows for the upload tray. */
export const trayEntries = batch => batch.files.map(file => ({
  key: `import-${file.uploadId}`,
  name: file.filename,
  size: file.filesize,
  phase: phaseOf(file),
  progress: 0
}))

const statusesOf = batch => batch.files.map(({status, uploadStatus}) => `${status}/${uploadStatus}`).join()

/**
 * Starts imports from Google Drive or OneDrive and follows the one running, for the upload tray and,
 * once it ends, the results.
 *
 * A person has one import at a time. "start" hands the API the picked files, with the provider they
 * came from and, for OneDrive, how long the token has left; it answers with the
 * files it took on and those it left out, and rejects with the API's refusal when it took on none
 * (see "refusalOf"). While the import runs it is asked after every few seconds, and the listings are
 * refreshed whenever a file moves on, so imported files appear as they're secured. Once it ends,
 * "results" holds every file it took on, as it ended, and those left out at the start.
 *
 * An import keeps running after the tab closes, so a drive that opens with one under way picks it
 * up again, though what was left out at its start is only known to the tab that started it.
 * "starting" names the provider while the API is still looking over the picked files, which for a
 * large pick takes a while, before there is a batch to show. "enabled" is whether the person may import at all; nothing is asked before it is known.
 */
const useImports = ({enabled}) => {
  const client = useApolloClient()
  const [startImport] = useMutation(START_IMPORT)
  const {data: activeData} = useQuery(ACTIVE_IMPORT, {skip: !enabled, fetchPolicy: 'network-only'})
  const [batch, setBatch] = useState(null)
  const [results, setResults] = useState(null)
  const [starting, setStarting] = useState(null)
  const leftOut = useRef([])
  const latestBatch = useRef(batch)

  useEffect(() => {
    latestBatch.current = batch
  })

  const refreshListings = () => client.refetchQueries({include: [FOLDERS, STORAGE, UPLOADS]})

  const end = ended => {
    setResults({provider: ended.provider, files: ended.files, skipped: leftOut.current})
    refreshListings()
  }

  useEffect(() => {
    if (activeData?.activeImport && !batch) setBatch(activeData.activeImport)
  }, [activeData])

  const start = ({provider, accessToken, expiresIn = null, fileIds, folderId}) => {
    setStarting(provider)

    return startImport({variables: {input: {provider, accessToken, expiresIn, fileIds, folderId}}})
      .then(({data}) => {
        const {skipped, ...started} = data.startImport

        leftOut.current = skipped
        setBatch(started)

        if (started.active) refreshListings()
        else end(started)
      })
      .finally(() => setStarting(null))
  }

  const batchId = batch?.active ? batch.id : null

  useEffect(() => {
    if (!batchId) return

    const timer = setInterval(() => client.query({query: IMPORT_BATCH, variables: {id: batchId}, fetchPolicy: 'network-only'})
      .then(({data}) => {
        const next = data.importBatch

        if (!next) return
        if (next.active && statusesOf(latestBatch.current) !== statusesOf(next)) refreshListings()

        setBatch(next)

        if (!next.active) end(next)
      })
      // Asked again on the next round.
      .catch(() => {}), POLL_INTERVAL)

    return () => clearInterval(timer)
  }, [batchId])

  return {
    active: Boolean(batch?.active),
    starting,
    provider: batch?.provider ?? null,
    entries: batch ? trayEntries(batch) : [],
    results,
    start,
    // A running import stays in the tray: dismissing it would hide what is still going on.
    dismiss: () => setBatch(current => (current?.active ? current : null)),
    closeResults: () => setResults(null)
  }
}

export default useImports
